import { CSSProperties, KeyboardEvent, MouseEvent, ReactNode, useEffect, useRef, useState } from 'react';
import { uniqueId } from 'lodash';

interface ITooltip {
    children: ReactNode;
    width: string;
    buttonText: string;
    buttonHtml?: ReactNode;
    buttonClasses?: string;
}

/**
 * Tooltip Widget
 */
const Tooltip = (props: ITooltip) => {

    const [isVisible, setIsVisible] = useState(false);
    const [tooltipId] = useState(() => uniqueId('tooltip-'));
    const [isRightAligned, setIsRightAligned] = useState(false);

    const tooltipRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);

    const stopPropagation = (evt: MouseEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
    };

    /**
     * on button click ---> open tooltip
     */
    const handleButtonClick = (evt: MouseEvent) => {
        evt.stopPropagation();
        evt.preventDefault();
        setIsVisible(true);
    };

    /**
     * perform tooltip close on outside click
     */
    useEffect(() => {
        const handleClick = () => {
            setIsVisible(false);
        };

        document.addEventListener('click', handleClick);

        return () => {
            document.removeEventListener('click', handleClick);
        };
    }, []);

    useEffect(() => {
        if(!isVisible) return;
        tooltipRef.current?.focus();

        const $tooltip = tooltipRef.current;
        const $button = buttonRef.current;
        if(!$tooltip || !$button) return;

        const tooltipWidth = $tooltip.offsetWidth;
        const buttonRect = $button.getBoundingClientRect();
        const spaceOnRight = window.innerWidth - buttonRect.left;

        setIsRightAligned(spaceOnRight < tooltipWidth);
    }, [isVisible]);


    const onKeyUp = (evt: KeyboardEvent<HTMLDivElement>) => {
        if(evt.key === 'Escape') {
            setIsVisible(false);
        }
    };

    const styles: CSSProperties = {
        width: props.width,
        [isRightAligned ? 'right' : 'left']: '0px',
    };

    return (
        <div className="relative" onMouseDown={ stopPropagation } onClick={ stopPropagation }>
            {
                <button
                    ref={ buttonRef }
                    aria-describedby={isVisible ? tooltipId : undefined}
                    aria-expanded={isVisible}
                    onClick={ handleButtonClick }
                    type="button"
                    className={ `text-blue-200 ${ props.buttonClasses ?? '' }` }>
                    { props.buttonHtml ?? props.buttonText }
                </button>
            }
            {
                isVisible &&
                <div
                    tabIndex={ -1 }
                    onKeyUp={ onKeyUp }
                    role="tooltip"
                    id={ tooltipId }
                    ref={ tooltipRef }
                    className="text-sm bg-gray-600 text-white p-3 text-center absolute pointer"
                    style={ styles }>
                        { props.children }
                    </div>
                }
            </div>
        )
};

export default Tooltip;